<template>
  <Layout>
    <el-row id="domains-view">
      <el-col :span="24" :md="{ span: 22, offset: 1 }">
        <el-row>
          <el-col :xs="24" :sm="12">
            <h1 class="has-subheading">Your domains</h1>
            <p>Manage the domains where you use onvocado</p>
          </el-col>
          <el-col :xs="24" :sm="12">
            <div class="mb-4 mx-4 md:mt-16 md:mr-10 flex flex-col justify-end text-right">
              <template v-if="!getPlanLimitsCompleted">
                <Loader></Loader>
              </template>
              <template v-else>
                <el-popover
                    placement="bottom"
                    title="Limit Reached"
                    width="200"
                    trigger="hover"
                    content="You have reached your active sites plan limit"
                    :disabled="!isFeatureLimitedDomains">
                  <strong slot="reference" class="text-3xl" :class="{ 'color-danger': isFeatureLimitedDomains }">
                    <i v-if="isFeatureLimitedDomains" class="el-icon-warning-outline"></i>
                    {{ domainsCount.current }} / {{ domainsCount.limit }}
                  </strong>
                </el-popover>
                <span class="text-sm">Active sites</span>
              </template>
            </div>
          </el-col>
        </el-row>

        <div class="container no-padding">
          <section class="mb-8">
            <div class="my-4 p-2 space-x-10 flex bg-onvocado-white border">
              <el-input v-model="newDomain" class="!w-1/2 gray" placeholder="Your domain"/>
              <button
                  class="btn primary"
                  type="button"
                  :disabled="newDomain.length === 0"
                  @click.stop="addDomain">
                Add new domain
              </button>
            </div>
          </section>

          <template v-if="domainActionLoading || embedCodeCheckLoading">
            <Loader></Loader>
          </template>
          <template v-else>
            <h3>Current domains</h3>
            <div v-for="(domain, index) in domains" :key="domain.id"
                 class="my-4 py-4 px-4 space-x-4 flex bg-onvocado-white border">
              <div class="w-16 pt-3 pr-4 relative flex justify-center border-r-2">
                <el-switch
                    active-color="#C8E85E"
                    inactive-color="#000000"
                    :value="domain.enabled"
                    @change="triggerSetDomainStatus(domain.id, !domain.enabled)"></el-switch>
              </div>
              <div class="flex-1 flex">
                <h4 class="mr-4 font-semibold">{{ domain.url }}</h4>
                <button @click="triggerUpdateDomain(index)">
                  <i class="el-icon-edit text-xl"></i>
                </button>
              </div>
              <button @click="triggerDeleteDomain(domain.id)">
                <i class="el-icon-delete text-xl"></i>
              </button>
            </div>
          </template>
        </div>
      </el-col>
    </el-row>
  </Layout>
</template>

<script>
import Layout from '@layouts/main.vue'
import { mapState, mapActions } from 'vuex'
import { CAMPAIGN_STATUS } from '@meta/campaignConstants'

export default {
  page: {
    title: 'Domains',
    meta: [{ name: 'description', content: 'Domains' }],
  },
  name: 'PixelView',
  components: {
    Layout,
  },
  data() {
    return {
      newDomain: '',
      domainEnabled: false,
    }
  },
  computed: {
    ...mapState('domain', [
      'domains',
      'domainActionCompleted',
      'domainActionLoading',
      'domainActionError',
      'domainErrorMessage',
    ]),
    ...mapState({
      embedCodeCheckLoading: (state) => state.campaign.embedCode.embedCodeCheckLoading,
      allCampaignDomains: (state) => state.campaign.embedCode.embedCodeCampaigns,
      domainsCount: (state) => state.plans.featureLimits.domains,
      getPlanLimitsCompleted: (state) => state.plans.featureLimits.getPlanLimitsCompleted,
    }),
    isFeatureLimitedDomains() {
      return this.$store.getters['plans/featureLimits/isFeatureLimited.domains']
    },
  },
  methods: {
    ...mapActions('domain', [
      'listDomains',
      'updateDomain',
      'createDomain',
      'deleteDomain',
    ]),
    ...mapActions('plans/featureLimits', [
      'getPlanLimits',
    ]),
    async confirmAction(action, message, title, confirmButtonText, cancelButtonText, type = 'warning') {
      try {
        await this.$msgbox.confirm(message, title, {
          confirmButtonText,
          cancelButtonText,
          showCancelButton: true,
          type,
        })
        action()
      } catch (error) {
        // console.log('Action cancelled')
      }
    },
    triggerSetDomainStatus(domainID, enabled) {
      const updatedDomain = { id: domainID, enabled: enabled }

      const affectedCampaigns = this.allCampaignDomains.filter(d => {
        return d.domainID === domainID && d.campaignStatus === CAMPAIGN_STATUS.ACTIVE
      })

      if (!enabled && affectedCampaigns.length > 0) {
        const campaignList = affectedCampaigns.map(campaign => campaign.campaignName).join(', ')

        const warningMessage = `<p class="text-lg">
        Deactivating this domain will deactivate its campaigns. <br><br>
        The following campaigns will be affected: <strong>${campaignList}</strong>.</p>`

        this.$msgbox({
          title: 'Confirm Deletion',
          message: warningMessage,
          dangerouslyUseHTMLString: true,
          showCancelButton: true,
          confirmButtonText: 'Deactivate',
          cancelButtonText: 'Cancel',
          type: 'warning',
          beforeClose: (action, instance, done) => {
            if (action === 'confirm') {
              this.setDomainStatus(updatedDomain, done)
            } else {
              done()  // Close the message box without doing anything
            }
          },
        }).catch(() => {
          // Handle the case where the MessageBox is closed without making a choice
        })
      } else {
        this.setDomainStatus(updatedDomain)
      }
    },
    setDomainStatus(updatedDomain, done) {
      this.updateDomain(updatedDomain).then(() => {
        if (!this.domainActionError) {
          this.$message({
            type: 'success',
            message: 'Domain successfully ' + (updatedDomain.enabled ? 'enabled' : 'disabled'),
          })
        } else {
          this.$message({
            type: 'error',
            message: this.domainErrorMessage || 'Failed to change domain status',
          })
        }

        this.getPlanLimits('domains')
      }).finally(done)
    },
    triggerUpdateDomain(index) {
      this.$msgbox
          .prompt('', 'Rename this domain', {
            confirmButtonText: 'OK',
            cancelButtonText: 'Cancel',
            inputValue: this.domains[index].url,
          })
          .then(({ value }) => {
            const newDomain = value.trim(' ')

            if (newDomain !== this.domains[index].url) {
              const updatedDomain = { id: this.domains[index].id, url: newDomain }

              this.updateDomain(updatedDomain).then(() => {
                if (!this.domainActionError) {
                  this.$message({
                    type: 'success',
                    message: 'Domain successfully renamed to \'' + newDomain + '\'',
                  })
                } else {
                  this.$message({
                    type: 'error',
                    message: this.domainErrorMessage,
                  })
                }
              })
            }
          })
    },
    addDomain() {
      const newDomain = this.newDomain.trim(' ')

      if (newDomain) {
        this.createDomain({ url: newDomain }).then(() => {
          if (!this.domainActionError) {
            this.newDomain = ''
            this.listDomains()
          } else {
            this.$message({
              type: 'error',
              message: this.domainErrorMessage,
            })
          }
        })
      }
    },
    triggerDeleteDomain(domainID) {
      const affectedCampaigns = this.allCampaignDomains.filter(d => d.domainID === domainID)
      let warningMessage = 'Are you sure you\'d like to delete this domain?'

      if (affectedCampaigns.length > 0) {
        const campaignList = affectedCampaigns.map(campaign => campaign.campaignName).join(', ')

        warningMessage = `<p class="text-lg">
        Deleting this domain will deactivate its campaigns, and you'll need to choose new domains for them. <br><br>
        The following campaigns will be affected: <strong>${campaignList}</strong>.</p>`
      }

      // Use MessageBox for confirmation with the customized warning message
      this.$msgbox({
        title: 'Confirm Deletion',
        message: warningMessage,
        dangerouslyUseHTMLString: true,
        showCancelButton: true,
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        type: 'warning',
        beforeClose: (action, instance, done) => {
          if (action === 'confirm') {
            this.deleteDomain(domainID).then(() => {
              // Handle successful deletion, e.g., show success message
              this.$message({
                type: 'success',
                message: 'Domain successfully deleted',
              })
              this.getPlanLimits('domains')
            }).catch((error) => {
              // Handle deletion error, e.g., show error message
              this.$message({
                type: 'error',
                message: error.message || 'Failed to delete domain',
              })
            }).finally(done)  // Make sure to call done() to close the message box
          } else {
            done()  // Close the message box without doing anything
          }
        },
      }).catch(() => {
        // Handle the case where the MessageBox is closed without making a choice
      })
    },
  },
}
</script>